import { EventListItem } from '@/types/event';
import { GroupListItem } from '@/types/group';
import { Box, Stack, SxProps, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useRouter } from 'next/router';
import React, { Suspense } from 'react';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import GPLink from '../GPLink';
import type { UserRegistration } from './User/RegistrationUserCard';

const LazyEventPublicCard = React.lazy(() => import('./EventPublicCard'));
const LazyGroupUserCard = React.lazy(() => import('./User/GroupUserCard'));
const LazyGroupPublicCard = React.lazy(() => import('./Public/GroupPublicCard'));
const LazyRegistrationUserCard = React.lazy(() => import('./User/RegistrationUserCard'));

export type PlaceInterestType = (GroupListItem | UserRegistration | EventListItem) & {
  unique_id?: number | string;
};

export type PlaceOfInterest = {
  interests?: PlaceInterestType[];
};

// 👇 useComponent pattern should help with preserving memo-ization of one-time computation
// even in the face of an unstable flag frequently destroying the returned component
export function usePlaceOfInterestCard() {
  const { pathname } = useRouter();

  // this is the one-time computation referred above
  const InfoCard = React.useMemo(() => {
    /**
     * Instead of following the existing pattern in MainBaseTab to prop
     * drill custom components down, going to dynamically render based on
     * the current path. If we're going to further support custom InfoWindow's
     * elsewhere, we should re-factor this to be more extensible.
     */
    const LazyInfoCardComponent = (() => {
      switch (pathname) {
        // all pages where RegistrationUserCard should be rendered as Google Map InfoWindow
        case '/dashboard/registrations':
          return LazyRegistrationUserCard;

        // all pages where GroupUserCard should be rendered as Google Map InfoWindow
        case '/dashboard/groups':
          return LazyGroupUserCard;

        // all pages where GroupPublicCard should be rendered as Google Map InfoWindow
        case '/search/groups/[[...location]]':
        case '/group/affiliates/[id]':
        case '/group/subgroups/[id]':
          return LazyGroupPublicCard;

        // everywhere else, render EventPublicCard as Google Map InfoWindow
        default:
          return LazyEventPublicCard;
      }
    })();

    return function InfoCard({ info }: InfoCardProps) {
      return !info ? null : (
        <Suspense fallback={<FallbackInfoCard info={info} />}>
          <LazyInfoCardComponent {...(info as UserRegistration & GroupListItem & EventListItem)} />
        </Suspense>
      );
    };
  }, [pathname]);

  // the returned component referred above
  return function PlaceOfInterestCard({ interests = [] }: PlaceOfInterest) {
    const { classes } = useStyles();
    const isViewportBig = useMediaQuery(useTheme().breakpoints.up('md'));

    const ifNoInterests = interests.length == 0;
    const exactlyOneInterest = interests.length == 1;
    const hasManyInterests = interests.length > 1;

    return ifNoInterests ? null : exactlyOneInterest ? (
      <InfoCard info={interests[0]} />
    ) : hasManyInterests ? (
      <Swiper
        modules={[Navigation, Pagination, A11y]}
        pagination={{
          clickable: isViewportBig,
        }}
        navigation
        a11y={{ enabled: true }}
        noSwiping={isViewportBig}
        noSwipingSelector=".swiper-slide"
        className={classes.swiper}
      >
        {interests.map((interest, i) => (
          <SwiperSlide key={interest.unique_id ?? i}>
            <InfoCard info={interest} />
          </SwiperSlide>
        ))}
      </Swiper>
    ) : null;
  };
}

type InfoCardProps = {
  info?: PlaceInterestType;
};

function FallbackInfoCard(props: InfoCardProps) {
  const info = props.info as Partial<EventListItem & GroupListItem>;
  const sx: SxProps = {
    image: {
      width: 270,
      height: 150,
      borderRadius: 6,
      marginBottom: 10,
    },
  };
  return !info ? null : (
    <Stack
      sx={{
        gap: 1,
        alignItems: "center"
      }}>
      <GPLink href={info.path} underline="hover" color="text.primary">
        <Typography variant="h3" align="center">
          {info.title}
        </Typography>
      </GPLink>
      <Box
        sx={sx}
        role="img"
        aria-label={info.title || ''}
        style={{
          backgroundImage: `url(${info.media?.cover?.url || info.cover?.url})`,
        }}
      />
    </Stack>
  );
}

const useStyles = makeStyles()(() => ({
  swiper: {
    cursor: 'pointer',
    '& .swiper-button-prev': {
      margin: 0,
      top: 0,
      left: 0,
      height: '100%',
      width: '15%',
      '&:after': {
        visibility: 'hidden', // only show arrow icons on hover
        fontSize: '25px',
        color: 'black',
      },
    },
    '& .swiper-button-next': {
      margin: 0,
      top: 0,
      right: 0,
      height: '100%',
      width: '15%',
      '&:after': {
        visibility: 'hidden', // only show arrow icons on hover
        fontSize: '25px',
        color: 'black',
      },
    },
    '&:hover': {
      '& .swiper-button-prev:after': {
        visibility: 'visible',
      },
      '& .swiper-button-next:after': {
        visibility: 'visible',
      },
    },
  },
}));
